import React, { useEffect, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';

/**
 * Figura de referência da tarefa de cópia: dois pentágonos que se cruzam,
 * formando uma interseção de quatro lados.
 *
 * Desenhada em SVG e não como imagem rasterizada, para escalar sem perder
 * traço quando ampliada — o que importa aqui, já que o critério de acerto
 * depende de contar ângulos e lados.
 *
 * A ampliação abre em fundo opaco, cobrindo o restante da página. É
 * deliberado: a figura é mostrada ao paciente, e o texto ao redor descreve
 * critérios de pontuação que ele não deve ler.
 */
const PENTAGONO_ESQUERDO = '114.2,29 157,88 114.2,147 44.8,124.4 44.8,51.6';
const PENTAGONO_DIREITO = '145.8,29 103,88 145.8,147 215.2,124.4 215.2,51.6';

function Desenho({ className, titulo }) {
  return (
    <svg
      viewBox="0 0 260 176"
      className={className}
      role="img"
      aria-label={titulo}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{titulo}</title>
      <polygon
        points={PENTAGONO_ESQUERDO}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <polygon
        points={PENTAGONO_DIREITO}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function PentagonFigure() {
  const [ampliada, setAmpliada] = useState(false);
  const titulo = 'Dois pentágonos que se cruzam, com interseção de quatro lados';

  useEffect(() => {
    if (!ampliada) return undefined;

    const aoTeclar = (e) => {
      if (e.key === 'Escape') setAmpliada(false);
    };
    const overflowAnterior = document.body.style.overflow;

    document.addEventListener('keydown', aoTeclar);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = overflowAnterior;
    };
  }, [ampliada]);

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setAmpliada(true)}
        className="group w-full flex flex-col items-center gap-2 p-4 rounded-xl border border-[var(--border)] bg-white hover:border-[var(--accent)] transition-colors"
        aria-haspopup="dialog"
      >
        <Desenho className="w-44 h-auto text-black" titulo={titulo} />
        <span className="text-xs font-bold text-black flex items-center gap-1.5">
          <SafeIcon name="Plus" className="w-3.5 h-3.5" />
          Ampliar para mostrar ao paciente
        </span>
      </button>
      <p className="text-[11px] text-[var(--text-muted)] leading-relaxed mt-2">
        Modelo de referência para a tarefa de cópia. Ampliada, a figura ocupa a tela
        inteira e cobre o restante da página, para que o paciente veja o desenho sem
        ler os critérios de pontuação.
      </p>

      {ampliada && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${titulo}. Ampliada para exibição ao paciente.`}
          className="fixed inset-0 z-[100] bg-white flex items-center justify-center p-6"
          onClick={() => setAmpliada(false)}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setAmpliada(false);
            }}
            aria-label="Fechar a figura ampliada"
            className="absolute top-5 right-5 w-12 h-12 flex items-center justify-center rounded-full border-2 border-[var(--border)] bg-white text-black hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
          >
            <SafeIcon name="X" className="w-6 h-6" />
          </button>

          <Desenho
            className="w-full max-w-3xl h-auto text-black"
            titulo={titulo}
          />

          <p className="absolute bottom-5 left-0 right-0 text-center text-xs text-[var(--text-muted)]">
            Toque em qualquer ponto da tela, ou pressione Esc, para fechar.
          </p>
        </div>
      )}
    </div>
  );
}
